import {
  Box,
  Divider,
  Flex,
  Grid,
  GridItem,
  Image,
  Input,
  InputGroup,
  InputRightElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";
import {
  useCoursesVisibleToAllUserGroupsQuery,
  useCreateUserGroupMutation,
  useListUserGroupsQuery,
} from "./index.generated";
import { CourseIcon } from "../../_shared/CourseIcon";
import folder from "./folder_icon.svg";
import { Link } from "react-router-dom";
import { AdminSidebar } from "../_shared/Sidebar";
import { UserGroup } from "~/generated/graphql.types";
import { BlueButton } from "../../_shared/Buttons";
import { RequestPolicy } from "urql";
import { SearchIcon } from "@chakra-ui/icons";

type UserGroupFragment = Pick<UserGroup, "name" | "id">;

export const AdminTop = () => {
  const listUserGroupsContext = useMemo(
    () => ({
      additionalTypenames: ["UserGroup"],
      requestPolicy: "network-only" as RequestPolicy,
    }),
    []
  );
  const [listUserGroupsRes] = useListUserGroupsQuery({
    context: listUserGroupsContext,
  });
  if (listUserGroupsRes.fetching) return <Text>loading...</Text>;
  const userGroup = listUserGroupsRes.data?.allUserGroups;
  if (!userGroup) return null;

  const context = useMemo(
    () => ({
      requestPolicy: "network-only" as RequestPolicy,
    }),
    []
  );
  const [visibleCoursesRes] = useCoursesVisibleToAllUserGroupsQuery({
    context,
  });
  const visibleCourses =
    visibleCoursesRes.data?.coursesVisibleToAllUserGroups || [];

  const isActiveRouteChangeAlertFlag = useMemo(() => {
    return visibleCoursesRes.data?.appConfig.routeChangeAlert;
  }, [visibleCoursesRes.data?.appConfig.routeChangeAlert]);

  const [searchedWord, setSearchedWord] = useState("");

  const [filteredCourses, setFilteredCourses] = useState(visibleCourses || []);

  useEffect(() => {
    if (searchedWord === "") {
      setFilteredCourses(visibleCourses);
      return;
    }
    const filtered = visibleCourses.filter((course) => {
      return course.name.includes(searchedWord);
    });
    setFilteredCourses(filtered);
  }, [searchedWord, visibleCoursesRes]);

  return (
    <Flex w="full">
      <AdminSidebar />
      <Box bgColor={"#16181a"} flexGrow={1} p={10}>
        <Box>
          <Flex
            flexDir="row"
            flexWrap="wrap"
            alignContent="center"
            justifyContent="space-between"
            py={2}
            my={4}
          >
            <Text fontSize={40} color="white" fontWeight={"bold"} mb={8}>
              特約店一覧
            </Text>
            <Flex gap={2} justifyItems="center">
              <CreateUserGroupModal />
            </Flex>
          </Flex>
          <Grid templateColumns="repeat(5, 1fr)" gap={8}>
            {userGroup.map((userGroup) => {
              return <OneUserGroup key={userGroup.id} userGroup={userGroup} />;
            })}
          </Grid>
        </Box>
        <Divider my="8" />
        {/* Other users' courses */}
        <Box py={4}>
          <Flex
            flexDir="row"
            flexWrap="wrap"
            justifyContent="space-between"
            py={2}
            my={4}
            alignItems="center"
          >
            <Text fontSize="4xl" fontWeight="bold" noOfLines={1} color="white">
              各特約店のリリース済みのコース
            </Text>
            <InputGroup
              maxW={300}
              w={"full"}
              display={{ base: "none", md: "flex" }}
            >
              <Input
                placeholder="検索"
                _placeholder={{ color: "gray.500" }}
                onChange={(event) => {
                  setSearchedWord(event.target.value);
                }}
                color="white"
              />

              <InputRightElement
                pointerEvents="none"
                children={<SearchIcon color="gray.500" />}
              />
            </InputGroup>
          </Flex>
          {filteredCourses.length === 0 ? (
            <Text fontSize={20} fontWeight="bold" color={"white"}>
              該当するコースが存在しません。
            </Text>
          ) : (
            <Grid templateColumns="repeat(5, 1fr)" gap={8} mb={4} w="full">
              {filteredCourses.map((course) => {
                return (
                  <CourseIcon
                    key={course.id}
                    course={course}
                    userGroupName={course.userGroup.name}
                    isActiveRouteChangeAlertFlag={isActiveRouteChangeAlertFlag}
                  />
                );
              })}
            </Grid>
          )}
        </Box>
      </Box>
    </Flex>
  );
};

const OneUserGroup = ({ userGroup }: { userGroup: UserGroupFragment }) => {
  return (
    <GridItem w="100%" overflow="hidden">
      <Link to={`/admin/userGroups/${userGroup.id}`} key={userGroup.id}>
        <Box justifyContent={"center"} border="1px" borderColor="white">
          <Image src={folder} m={"15%"} />
        </Box>
        <Text
          color={"gray.50"}
          fontWeight="bold"
          fontSize={"l"}
          mt="10%"
          whiteSpace={"nowrap"}
        >
          {userGroup.name}
        </Text>
      </Link>
    </GridItem>
  );
};

const CreateUserGroupModal = () => {
  const toast = useToast();

  const [createUserGroupRes, createUserGroup] = useCreateUserGroupMutation();

  const [userGroupName, setUserGroupName] = useState("");
  const { isOpen, onOpen, onClose } = useDisclosure();
  useEffect(() => {
    if (createUserGroupRes.data) {
      toast({
        title: "特約店を作成しました。",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    }
  }, [createUserGroupRes.data]);

  return (
    <>
      <BlueButton onClick={onOpen} label={"新しく特約店を作成"}></BlueButton>

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>特約店の作成</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>新しい特約店を作成します。特約店名を入力してください。</Text>
            <Input
              placeholder={"特約店名"}
              onChange={(event) => {
                setUserGroupName(event.target.value);
              }}
            />
          </ModalBody>

          <ModalFooter>
            <Flex>
              <BlueButton
                onClick={() => {
                  if (userGroupName.replaceAll(" ", "") === "") {
                    toast({
                      title: "特約店名を入力してください。",
                      status: "error",
                      duration: 5000,
                      isClosable: true,
                    });
                  } else {
                    createUserGroup({ input: { name: userGroupName } });
                    onClose();
                  }
                }}
                label={"作成"}
              ></BlueButton>
            </Flex>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
